import { AppSettings } from "@/AppSettings";
import { ConfigurationSourceDataItemGroup, ConfigurationSourceDataMap, Metadata, SourceDataItemGroupType, SourceDataPriority, Trial } from "@/api/dtos";
import { useMemo } from "react";
import { ConfigurationType } from ".";


const excludedConfigurationSourceDataItemGroupCopyProperties: Partial<Record<keyof ConfigurationSourceDataItemGroup, boolean>> = {
    id: true,
    trialId: true,
    configurationSourceDataId: true,
    studyEventOid: true,
    caseReportFormOid: true,
    caseReportFormVersionOid: true,
    itemGroupName: true,
    linked: true,
    enteredBy: true,
    enteredDate: true,
    modifiedBy: true,
    modifiedDate: true
}

export const copyConfigurationSourceDataItemGroupSettings = (
    source: ConfigurationSourceDataItemGroup,
    target: ConfigurationSourceDataItemGroup,

) => {
    for (let pName in source) {
        const propertyName = pName as keyof ConfigurationSourceDataItemGroup;

        if (!excludedConfigurationSourceDataItemGroupCopyProperties[propertyName]) {
            (target[propertyName] as any) = source[propertyName] as any;
        }
    }

    return target;
};

export const createItemGroupConfiguration = (
    metadata?: Metadata, 
    trialConfigurationItemGroups?: ConfigurationSourceDataMap['itemGroups'], 
    configurationItemGroups?: ConfigurationSourceDataMap['itemGroups'], 
    trial?: Trial,  
    isGlobal?: boolean,
    configurationSourceDataId?: number, 
    studyEventOid?: string,
    caseReportFormOid?: string,
    caseReportFormVersionOid?: string,
    itemGroupName?: string
) => {
    
    if (!metadata || !metadata.itemGroups || !caseReportFormOid || !caseReportFormVersionOid || !itemGroupName) {
        return undefined;
    }

    const itemGroupMetadata = metadata.itemGroups?.[caseReportFormOid]?.[caseReportFormVersionOid]?.[itemGroupName];

    if (!itemGroupMetadata) {
        return undefined;
    }

    const itemGroupConfiguration = configurationItemGroups?.[AppSettings.metadata.studyEventNullKey]?.[caseReportFormOid]?.[caseReportFormVersionOid]?.[itemGroupName];
    const eventItemGroupConfiguration = studyEventOid ? configurationItemGroups?.[studyEventOid]?.[caseReportFormOid]?.[caseReportFormVersionOid]?.[itemGroupName] : undefined;

    const trialItemGroupConfiguration = trialConfigurationItemGroups?.[AppSettings.metadata.studyEventNullKey]?.[caseReportFormOid]?.[caseReportFormVersionOid]?.[itemGroupName];
    const trialEventItemGroupConfiguration = studyEventOid ? trialConfigurationItemGroups?.[studyEventOid]?.[caseReportFormOid]?.[caseReportFormVersionOid]?.[itemGroupName] : undefined;

    const dominantConfiguration = eventItemGroupConfiguration?.linked === false ? 
        eventItemGroupConfiguration : 
        trialEventItemGroupConfiguration?.linked === false ?
            trialEventItemGroupConfiguration :
            itemGroupConfiguration?.linked === false ?
                itemGroupConfiguration :
                trialItemGroupConfiguration

    const configurationLink = studyEventOid ? 
        `/${trial?.oid}/configuration/source-data/${isGlobal ? 'trial' : configurationSourceDataId ? configurationSourceDataId : 'new'}/events/${studyEventOid}/forms/${itemGroupMetadata?.caseReportFormOid}/versions/${itemGroupMetadata.caseReportFormVersionOid}/groups/${itemGroupMetadata.itemGroupName}` :
        `/${trial?.oid}/configuration/source-data/${isGlobal ? 'trial' : configurationSourceDataId ? configurationSourceDataId : 'new'}/forms/${itemGroupMetadata?.caseReportFormOid}/versions/${itemGroupMetadata.caseReportFormVersionOid}/groups/${itemGroupMetadata.itemGroupName}`;

    return {
        itemGroupMetadata: itemGroupMetadata,
        itemGroupConfiguration: itemGroupConfiguration,
        eventItemGroupConfiguration: eventItemGroupConfiguration,
        trialItemGroupConfiguration: trialItemGroupConfiguration,
        trialEventItemGroupConfiguration: trialEventItemGroupConfiguration,
        configuration: dominantConfiguration,

        linked: studyEventOid ?
            eventItemGroupConfiguration?.linked !== false :
            itemGroupConfiguration?.linked !== false,

        type: eventItemGroupConfiguration?.linked === false ?
            ConfigurationType.Event :
            trialEventItemGroupConfiguration?.linked === false ?
                ConfigurationType.TrialEvent :
                itemGroupConfiguration?.linked === false ?
                    ConfigurationType.Form :
                    ConfigurationType.Trial,

        icon: itemGroupMetadata.type === SourceDataItemGroupType.Grid ? AppSettings.icons.itemGrid : AppSettings.icons.itemGroup,

        studyEventOid: studyEventOid,
        caseReportFormOid: itemGroupMetadata.caseReportFormOid,
        caseReportFormVersionOid: itemGroupMetadata.caseReportFormVersionOid,
        itemGroupName: itemGroupMetadata.itemGroupName,

        displayName: dominantConfiguration?.name ?? itemGroupMetadata?.name,

        priority: dominantConfiguration?.priority,

        configurationLink: configurationLink,
        configurationItemsLink: `${configurationLink}/items`
    };
}

const useItemGroupConfiguration = (
    metadata?: Metadata, 
    trialConfiguration?: ConfigurationSourceDataMap, 
    configuration?: ConfigurationSourceDataMap, 
    trial?: Trial, 
    configurationSourceDataId?: number, 
    studyEventOid?: string,
    caseReportFormOid?: string,
    caseReportFormVersionOid?: string,
    itemGroupName?: string
) => {
    return  useMemo(() => {
        return createItemGroupConfiguration(
            metadata, 
            trialConfiguration?.itemGroups, 
            configuration?.itemGroups, 
            trial, 
            configuration?.configuration?.isGlobal,
            configuration?.configuration?.id ?? configurationSourceDataId, 
            studyEventOid,
            caseReportFormOid,
            caseReportFormVersionOid,
            itemGroupName
        );
    }, [metadata, trialConfiguration?.itemGroups, configuration?.itemGroups, studyEventOid, caseReportFormOid, caseReportFormVersionOid, itemGroupName, trial, configurationSourceDataId]);
    
};

export default useItemGroupConfiguration;